import { Component, type ErrorInfo, type ReactNode } from 'react';

// resetKey changes on navigation (the caller passes the current pathname). When it
// changes we clear the error so the user can leave a broken page without a full reload.
type Props = { children: ReactNode; resetKey?: string };
type State = { hasError: boolean; resetKey?: string };

// App-wide safety net. Without it, any error thrown during render propagates to the
// root, React unmounts the whole tree, and the user is left staring at a blank page
// that only a manual refresh recovers. This catches that render throw and swaps in a
// recoverable card so a single bad render can't take the app down.
//
// A class component because error boundaries have no hooks equivalent —
// getDerivedStateFromError / componentDidCatch are the only React API for this.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  // Clear the error when resetKey changes (i.e. the route changed). resetKey is a plain
  // prop, NOT a React key, so normal navigation re-renders the children in place rather
  // than remounting them — in-page focus/state (e.g. the autocomplete's retained focus)
  // survives; only an error-then-navigate actually resets.
  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Nothing else surfaces a render throw once we've swapped in the fallback, so log
    // it for anyone with the console open (and any future error-reporting hook).
    // eslint-disable-next-line no-console -- last-resort diagnostics for a caught render error
    console.error('Unhandled render error caught by ErrorBoundary:', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex w-full justify-center py-16">
        <div className="max-w-md rounded-lg border border-base-200 bg-base-100 p-6 text-center">
          <div className="text-error text-lg font-bold mb-2">Something went wrong</div>
          <p className="text-sm text-base-content/70 mb-4">
            The page hit an unexpected error. Reloading usually clears it; if it keeps happening,
            contact IT Ops.
          </p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
